import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrackingData } from "@/core/dtos/tracking-data";
import { cn } from "@/lib/utils";
import {
  Check,
  Copy,
  ExternalLink,
  Globe,
  Megaphone,
  MousePointerClick,
  ShieldCheck,
} from "lucide-react";
import React, { useMemo, useState } from "react";

interface TrackingDisplayProps {
  tracking?: TrackingData;
  className?: string;
}

type AttributionQuality = "click" | "browser" | "identity";

interface ChannelSummary {
  label: string;
  quality: AttributionQuality;
  detail: string;
}

const QUALITY_BADGE: Record<
  AttributionQuality,
  { variant: "default" | "secondary" | "outline"; text: string }
> = {
  click: { variant: "default", text: "Click ID" },
  browser: { variant: "secondary", text: "Browser only" },
  identity: { variant: "outline", text: "No click data" },
};

/**
 * What the platforms can actually do with this deal.
 *
 * A click ID (fbc / gclid) ties the conversion to a specific ad. A browser
 * cookie alone (fbp) only identifies the browser, so the platform has to match
 * on identity and campaign-level attribution is unreliable. Neither means we
 * are relying on hashed email and phone alone.
 */
function summariseChannels(tracking: TrackingData): ChannelSummary[] {
  const summaries: ChannelSummary[] = [];
  const { meta, googleAds } = tracking;

  if (meta?.fbc) {
    summaries.push({
      label: "Meta",
      quality: "click",
      detail: "Click ID captured — this deal can be attributed to an ad",
    });
  } else if (meta?.fbp) {
    summaries.push({
      label: "Meta",
      quality: "browser",
      detail: "Browser cookie only — no ad click recorded for this visit",
    });
  }

  const googleClickId =
    googleAds?.gclid ?? googleAds?.wbraid ?? googleAds?.gbraid;
  if (googleClickId) {
    summaries.push({
      label: "Google Ads",
      quality: "click",
      detail: googleAds?.gclid
        ? "gclid captured"
        : "Braid captured (privacy-safe click id)",
    });
  }

  return summaries;
}

function consentLabel(tracking: TrackingData): {
  text: string;
  variant: "default" | "secondary" | "destructive" | "outline";
} {
  const value = tracking.privacy?.adPersonalization;

  if (value === true) {
    return { text: "Marketing consent granted", variant: "default" };
  }
  if (value === false) {
    return { text: "Marketing consent denied", variant: "destructive" };
  }
  return { text: "Consent not answered", variant: "outline" };
}

function CopyableValue({
  value,
  mono = true,
}: {
  value: string;
  mono?: boolean;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex items-start gap-1">
      <span
        className={cn(
          "text-sm text-muted-foreground break-all",
          mono && "font-mono text-xs",
        )}
      >
        {value}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy value"}
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-600" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

function FieldLabel({ children }: { children: string }): React.ReactElement {
  return (
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Renders only the identifiers that were actually captured.
 *
 * Most deals hold a small subset — wbraid, for instance, appears on a handful
 * of deals in a whole quarter — so laying out every possible field would bury
 * the two or three that matter under a column of dashes. The summary badges
 * above already say what is missing.
 */
function IdentifierSection({
  title,
  fields,
}: {
  title: string;
  fields: Array<{ label: string; value?: string }>;
}): React.ReactElement | null {
  const present = fields.filter(
    (field): field is { label: string; value: string } => Boolean(field.value),
  );

  if (present.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h5 className="text-sm font-semibold">{title}</h5>
      <div className="grid gap-4 sm:grid-cols-2">
        {present.map((field) => (
          <div key={field.label} className="space-y-1">
            <FieldLabel>{field.label}</FieldLabel>
            <CopyableValue value={field.value} />
          </div>
        ))}
      </div>
    </div>
  );
}

const TrackingDisplay: React.FC<TrackingDisplayProps> = ({
  tracking,
  className = "",
}) => {
  const channels = useMemo(
    () => (tracking ? summariseChannels(tracking) : []),
    [tracking],
  );

  if (!tracking) {
    return (
      <div
        className={cn(
          "border rounded-lg p-6 text-sm text-muted-foreground",
          className,
        )}
      >
        No tracking data was captured for this deal. Deals created directly in
        HubSpot have none, since there was no browser session.
      </div>
    );
  }

  const consent = consentLabel(tracking);
  const { campaign, client, meta, googleAds, ga4, privacy } = tracking;
  const location = [privacy?.region, privacy?.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className={cn("border rounded-lg p-6 space-y-6", className)}>
      {/* What can actually be attributed */}
      <div className="flex flex-wrap items-center gap-2">
        {channels.length > 0 ? (
          channels.map((channel) => (
            <Badge
              key={channel.label}
              variant={QUALITY_BADGE[channel.quality].variant}
              title={channel.detail}
            >
              <MousePointerClick className="h-3 w-3 mr-1" />
              {channel.label}: {QUALITY_BADGE[channel.quality].text}
            </Badge>
          ))
        ) : (
          <Badge variant="outline" title="Only hashed email and phone are sent">
            No ad identifiers captured
          </Badge>
        )}

        <Badge variant={consent.variant}>
          <ShieldCheck className="h-3 w-3 mr-1" />
          {consent.text}
        </Badge>

        {privacy?.inEu ? <Badge variant="secondary">EU / EEA</Badge> : null}

        {campaign?.utmCampaign ? (
          <Badge variant="secondary" title="utm_campaign on the landing page">
            <Megaphone className="h-3 w-3 mr-1" />
            {campaign.utmCampaign}
          </Badge>
        ) : null}
      </div>

      {channels.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          {channels.map((channel) => channel.detail).join(" · ")}
        </p>
      ) : null}

      {/* Campaign — the landing page is recorded separately from the page the
          form was submitted from, which is under Session below. */}
      {campaign?.utmCampaign ||
      campaign?.utmTerm ||
      campaign?.landingPageUrl ? (
        <div className="space-y-3">
          <h5 className="text-sm font-semibold">Campaign</h5>

          <div className="grid gap-4 sm:grid-cols-2">
            {campaign.utmCampaign ? (
              <div className="space-y-1">
                <FieldLabel>utm_campaign</FieldLabel>
                <CopyableValue value={campaign.utmCampaign} mono={false} />
              </div>
            ) : null}

            {campaign.utmTerm ? (
              <div className="space-y-1">
                <FieldLabel>utm_term</FieldLabel>
                <CopyableValue value={campaign.utmTerm} mono={false} />
              </div>
            ) : null}
          </div>

          {campaign.landingPageUrl ? (
            <div className="space-y-1">
              <FieldLabel>Landing page</FieldLabel>
              <a
                href={campaign.landingPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-muted-foreground break-all hover:underline inline-flex items-start gap-1"
              >
                {campaign.landingPageUrl}
                <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
              </a>
            </div>
          ) : null}
        </div>
      ) : null}

      <IdentifierSection
        title="Meta"
        fields={[
          { label: "fbc (click id)", value: meta?.fbc },
          { label: "fbp (browser id)", value: meta?.fbp },
        ]}
      />

      <IdentifierSection
        title="Google Ads"
        fields={[
          { label: "gclid", value: googleAds?.gclid },
          { label: "wbraid", value: googleAds?.wbraid },
          { label: "gbraid", value: googleAds?.gbraid },
        ]}
      />

      <IdentifierSection
        title="Google Analytics"
        fields={[
          { label: "Client ID", value: ga4?.clientId },
          { label: "Session ID", value: ga4?.sessionId },
        ]}
      />

      {/* Session — kept separate because the landing page and location are not
          plain copyable identifiers. */}
      {client?.ipAddress ||
      client?.userAgent ||
      client?.eventSourceUrl ||
      location ? (
        <div className="space-y-3">
          <h5 className="text-sm font-semibold">Session</h5>

          <div className="grid gap-4 sm:grid-cols-2">
            {client?.ipAddress ? (
              <div className="space-y-1">
                <FieldLabel>IP address</FieldLabel>
                <CopyableValue value={client.ipAddress} />
              </div>
            ) : null}

            {location ? (
              <div className="space-y-1">
                <FieldLabel>Location</FieldLabel>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {location}
                </span>
              </div>
            ) : null}
          </div>

          {client?.eventSourceUrl ? (
            <div className="space-y-1">
              {/* Not the landing page — this is wherever the form was actually
                  submitted from, which is often a different page. */}
              <FieldLabel>Submitted from</FieldLabel>
              <a
                href={client.eventSourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-muted-foreground break-all hover:underline inline-flex items-start gap-1"
              >
                {client.eventSourceUrl}
                <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
              </a>
            </div>
          ) : null}

          {client?.userAgent ? (
            <div className="space-y-1">
              <FieldLabel>User agent</FieldLabel>
              <CopyableValue value={client.userAgent} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TrackingDisplay;
